/**
 * SYNBIOTEX — hero slider
 *
 * Samostatný custom element bez závislostí. Registrace je idempotentní,
 * takže opakované načtení assetu (Theme Editor) nic nerozbije.
 * Všechny listenery se ruší v disconnectedCallback, interval taktéž.
 *
 * Analytický hook: element emituje bublající CustomEvent `sbx:hero`.
 * `event.detail.action` je slide_view, navigation, cta_click, product_click
 * nebo playback; vendor-specific tracking zůstá mimo komponentu.
 */
class SbxHeroSlider extends HTMLElement {
  #slides = [];
  #dots = [];
  #timer = null;
  #index = 0;
  #paused = false;        // uživatelem přes tlačítko
  #suspended = false;     // dočasně: hover, focus, skrytá karta
  #interval = 7000;
  #autoplay = false;
  #pauseOnHover = true;
  #touchX = null;
  #touchY = null;
  #abort = null;

  connectedCallback() {
    // idempotence — kdyby se element připojil dvakrát
    if (this.#abort) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#slides = Array.from(this.querySelectorAll('[data-sbxhs-slide]'));
    this.#dots = Array.from(this.querySelectorAll('[data-sbxhs-dot]'));
    if (!this.#slides.length) return;

    this.#interval = Math.min(12000, Math.max(5000, Number(this.dataset.interval) || 7000));
    this.#pauseOnHover = this.dataset.pauseOnHover !== 'false';

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // autoplay nemá smysl u jediného slidu a nespouští se při omezení pohybu
    const designMode = this.dataset.designMode === 'true';
    this.#autoplay = this.dataset.autoplay === 'true' && this.#slides.length > 1 && !reduced && !designMode;

    this.#index = Math.max(0, this.#slides.findIndex((s) => s.classList.contains('is-active')));
    this.#apply(this.#index, { announce: false, source: 'initial' });

    const prev = this.querySelector('[data-sbxhs-prev]');
    const next = this.querySelector('[data-sbxhs-next]');
    prev?.addEventListener('click', () => this.#step(-1, { user: true, source: 'previous' }), { signal });
    next?.addEventListener('click', () => this.#step(1, { user: true, source: 'next' }), { signal });

    this.#dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.#go(i, { user: true, source: 'dot' }), { signal });
    });

    this.querySelectorAll('[data-sbx-cta]').forEach((link) => {
      link.addEventListener('click', () => {
        this.#track('cta_click', {
          cta: link.dataset.sbxCta,
          slide: this.#index + 1,
          href: link.getAttribute('href'),
        });
      }, { signal });
    });

    this.querySelectorAll('[data-sbx-product-link]').forEach((link) => {
      link.addEventListener('click', () => {
        this.#track('product_click', {
          slide: this.#index + 1,
          href: link.getAttribute('href'),
        });
      }, { signal });
    });

    const toggle = this.querySelector('[data-sbxhs-toggle]');
    if (toggle) {
      if (!this.#autoplay) toggle.hidden = true;
      toggle.addEventListener('click', () => this.#togglePlay(toggle), { signal });
    }

    if (this.#slides.length > 1) {
      this.addEventListener('keydown', this.#onKey, { signal });
      this.addEventListener('touchstart', this.#onTouchStart, { passive: true, signal });
      this.addEventListener('touchend', this.#onTouchEnd, { passive: true, signal });
    }

    if (this.#pauseOnHover) {
      this.addEventListener('mouseenter', this.#suspend, { signal });
      this.addEventListener('mouseleave', this.#resume, { signal });
    }
    this.addEventListener('focusin', this.#suspend, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });

    document.addEventListener('visibilitychange', this.#onVisibility, { signal });

    // Theme Editor — výběr bloku zobrazí odpovídající slide a zastaví autoplay
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });

    this.#start();
  }

  disconnectedCallback() {
    if (this.dataset.drivesHeader !== 'false') delete document.documentElement.dataset.heroText;
    this.#stop();
    this.#abort?.abort();
    this.#abort = null;
  }

  /* ---------- přehrávání ---------- */

  #start() {
    this.#stop();
    if (!this.#autoplay || this.#paused || this.#suspended) return;
    this.#timer = window.setInterval(() => this.#step(1, { user: false, source: 'autoplay' }), this.#interval);
  }

  #stop() {
    if (this.#timer !== null) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  #suspend = () => {
    this.#suspended = true;
    this.#stop();
  };

  #resume = () => {
    this.#suspended = false;
    this.#start();
  };

  #onFocusOut = (event) => {
    // pauzu držíme, dokud je fokus kdekoli uvnitř slideru
    if (!this.contains(event.relatedTarget)) this.#resume();
  };

  #onVisibility = () => {
    if (document.hidden) this.#suspend();
    else this.#resume();
  };

  #togglePlay(button) {
    this.#paused = !this.#paused;
    button.setAttribute('aria-pressed', String(this.#paused));
    button.dataset.state = this.#paused ? 'paused' : 'playing';
    const label = this.#paused ? button.dataset.labelPlay : button.dataset.labelPause;
    if (label) button.setAttribute('aria-label', label);
    if (this.#paused) this.#stop();
    else this.#start();
    this.#track('playback', { state: this.#paused ? 'paused' : 'playing' });
  }

  /* ---------- navigace ---------- */

  #step(delta, { user = true, source = 'control' } = {}) {
    this.#go(this.#index + delta, { user, source });
  }

  #go(target, { user = false, source = 'programmatic' } = {}) {
    const total = this.#slides.length;
    if (total < 2) return;
    const next = ((target % total) + total) % total;
    if (next === this.#index) return;
    this.#index = next;
    this.#apply(next, { announce: user, source });
    if (user) this.#track('navigation', { source, slide: next + 1 });
    // po ruční interakci počítáme interval znovu od nuly
    if (user && this.#autoplay && !this.#paused) this.#start();
  }

  #apply(index, { announce = false, source = 'programmatic' } = {}) {
    this.#slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      // inert drží odkazy skrytých slidů mimo pořadí tabulátoru
      if (active) slide.removeAttribute('inert');
      else slide.setAttribute('inert', '');
    });

    this.#dots.forEach((dot, i) => {
      dot.setAttribute('aria-current', String(i === index));
    });

    // hlavička nad heroem se musí přebarvit podle pozadí slidu
    if (this.dataset.drivesHeader !== 'false') {
      const mode = this.#slides[index].dataset.mode;
      document.documentElement.dataset.heroText = mode === 'light' ? 'light' : 'dark';
    }

    const counter = this.querySelector('[data-sbxhs-current]');
    if (counter) counter.textContent = String(index + 1).padStart(2, '0');

    const live = this.querySelector('[data-sbxhs-live]');
    if (live && announce) {
      live.textContent = `${index + 1} / ${this.#slides.length}`;
    }

    const slide = this.#slides[index];
    this.#track('slide_view', {
      source,
      slide: index + 1,
      name: slide?.dataset.sbxSlideName || '',
    });
  }

  #track(action, detail = {}) {
    this.dispatchEvent(new CustomEvent('sbx:hero', {
      bubbles: true,
      detail: {
        action,
        sectionId: this.dataset.sectionId || '',
        ...detail,
      },
    }));
  }

  /* ---------- vstupy ---------- */

  #onKey = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(-1, { user: true, source: 'keyboard' });
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(1, { user: true, source: 'keyboard' });
    }
  };

  #onTouchStart = (event) => {
    const t = event.changedTouches[0];
    this.#touchX = t.clientX;
    this.#touchY = t.clientY;
  };

  #onTouchEnd = (event) => {
    if (this.#touchX === null) return;
    const t = event.changedTouches[0];
    const dx = t.clientX - this.#touchX;
    const dy = t.clientY - this.#touchY;
    this.#touchX = null;
    this.#touchY = null;
    // vodorovné gesto musí jasně převážit nad svislým scrollem
    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
    this.#step(dx < 0 ? 1 : -1, { user: true, source: 'swipe' });
  };

  /* ---------- Theme Editor ---------- */

  #onBlockSelect = (event) => {
    if (!this.contains(event.target)) return;
    const i = this.#slides.indexOf(event.target.closest('[data-sbxhs-slide]'));
    if (i > -1) {
      this.#index = i;
      this.#apply(i, { announce: false, source: 'theme-editor' });
    }
    this.#suspend();
  };

  #onBlockDeselect = (event) => {
    if (!this.contains(event.target)) return;
    this.#resume();
  };
}

if (!customElements.get('sbx-hero-slider')) {
  customElements.define('sbx-hero-slider', SbxHeroSlider);
}
